/*
  TaskBulkBar — the navy bar for selected tasks (Needs details card and Table view, idea 9):
    Assign · Project · Due date · Move · ⋯ Delete N tasks…
  Each change applies to every selected task in ONE request (POST /tasks/bulk-update or /tasks/bulk-delete),
  so the backend writes one undo entry and the shell's toast can undo the whole change.
  Assign collects one or more people, then "Add N people"; Project replaces the tasks' project, except for tasks
  made from a stage / the material list / an invoice — those keep theirs (the picker says so; the server refuses).
  Delete sits behind ⋯ and asks first (foundation rule: no red buttons in the bar).
*/
import { useState } from 'react';
import { ArrowRight, CalendarDays, FolderKanban, Trash2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BulkBar, BulkButton } from '@/components/ui/bulk-bar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDate } from '@/lib/format';
import { AssignPanel, DueDatePanel, MovePanel, ProjectPanel } from './TaskQuickPanels';
import { NO_SECTION, taskOrigin } from './taskModel';

const listNames = (items, limit = 2) => {
  const names = items.slice(0, limit).map((item) => `“${item.name}”`).join(', ');
  return items.length > limit ? `${names} +${items.length - limit}` : names;
};

const popoverClasses = 'w-[288px] rounded-xl border-line bg-surface p-0 pb-1 text-ink shadow-overlay';

function BulkPicker({ icon, label, open, onOpenChange, children, testId }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <BulkButton icon={icon} aria-expanded={open} data-testid={testId}>{label}</BulkButton>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className={popoverClasses}
        // keys in the picker (Escape, arrows) must not reach the bar (Escape there clears the selection)
        onKeyDown={(event) => event.stopPropagation()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export default function TaskBulkBar({
  tasks,
  members = [],
  projects = [],
  sections = [],
  onClear,
  returnFocus,
  onBulkUpdate,
  onBulkDelete,
  className,
  testId = 'task-bulk-bar',
}) {
  const [open, setOpen] = useState(null);
  const [people, setPeople] = useState([]); // picked in the Assign picker, added together
  const count = tasks.length;
  const noun = `${count} ${count === 1 ? 'task' : 'tasks'}`;
  const toggle = (key) => (next) => {
    setOpen(next ? key : null);
    if (key === 'assign') setPeople([]);
  };
  const apply = (payload, message) => {
    setOpen(null);
    setPeople([]);
    onBulkUpdate(tasks, payload, message);
  };
  const togglePerson = (member) => setPeople((current) => (current.some((item) => item.id === member.id)
    ? current.filter((item) => item.id !== member.id)
    : [...current, member]));
  const linked = tasks.filter((task) => taskOrigin(task));

  return (
    <BulkBar
      count={count}
      onClear={onClear}
      returnFocus={returnFocus}
      className={className}
      testId={testId}
      moreActions={[{
        label: `Delete ${noun}…`,
        icon: Trash2,
        danger: true,
        testId: `${testId}-delete`,
        confirm: {
          title: `Delete ${noun}?`,
          description: count <= 5
            ? `${tasks.map((task) => `“${task.name}”`).join(', ')} will be removed for everyone.`
            : `These ${count} tasks will be removed for everyone.`,
          confirmLabel: `Delete ${noun}`,
          note: 'You can undo this right after from the toast or from Recent changes.',
        },
        onSelect: () => onBulkDelete(tasks),
      }]}
    >
      <BulkPicker icon={UserPlus} label="Assign" open={open === 'assign'} onOpenChange={toggle('assign')} testId={`${testId}-assign`}>
        <AssignPanel
          title={`Add people to ${noun}`}
          members={members}
          selectedIds={people.map((member) => member.id)}
          onToggle={togglePerson}
          footer={(
            <div className="flex items-center gap-2 border-t border-line-2 px-3 pt-2">
              <span className="min-w-0 flex-1 truncate text-[12px] text-ink-3">
                {people.length ? people.map((member) => member.name).join(', ') : 'Pick one or more people'}
              </span>
              <Button
                type="button"
                size="sm"
                disabled={!people.length}
                onClick={() => apply(
                  { add_assignee_ids: people.map((member) => member.id) },
                  `${people.map((member) => member.name).join(', ')} added to ${noun}`,
                )}
                data-testid={`${testId}-assign-apply`}
              >
                {people.length > 1 ? `Add ${people.length} people` : 'Add'}
              </Button>
            </div>
          )}
        />
      </BulkPicker>
      <BulkPicker icon={FolderKanban} label="Project" open={open === 'project'} onOpenChange={toggle('project')} testId={`${testId}-project`}>
        <ProjectPanel
          single
          title={`Move ${noun} to project`}
          hint={linked.length
            ? `${listNames(linked)} came from a project (a stage, the material list or an invoice) and stay in it — unselect ${linked.length === 1 ? 'it' : 'them'} to move the others.`
            : 'Replaces their current project.'}
          disabled={linked.length > 0}
          projects={projects}
          onToggle={(project) => apply({ project_id: project.id }, `${noun} moved to ${project.name}`)}
        />
      </BulkPicker>
      <BulkPicker icon={CalendarDays} label="Due date" open={open === 'due'} onOpenChange={toggle('due')} testId={`${testId}-due`}>
        <DueDatePanel
          title={`Due date for ${noun}`}
          value=""
          onPick={(value) => apply({ due_date: value }, value ? `${noun} due ${formatDate(value, { year: 'auto' })}` : `Due date cleared on ${noun}`)}
        />
      </BulkPicker>
      <BulkPicker icon={ArrowRight} label="Move" open={open === 'move'} onOpenChange={toggle('move')} testId={`${testId}-move`}>
        <MovePanel
          title={`Move ${noun} to`}
          sections={sections}
          current={null}
          onPick={(section) => apply({ section: section === NO_SECTION ? '' : section }, `${noun} moved to ${section}`)}
        />
      </BulkPicker>
    </BulkBar>
  );
}
